import React from "react";
import ReactDOM from "react-dom/client";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import App from "./App";
import { BUILD_VERSION, PERSIST_MAX_AGE_MS, persister, queryClient, shouldDehydrateQuery } from "./lib/queryClient";

// Theme is resolved BEFORE this module ever runs — see index.html's own
// blocking inline <script> (a `type="module"` script like this one is
// always deferred until after HTML parsing, which is exactly the gap that
// caused the load-flash this replaced; see that script's comment for the
// full reasoning). global.css is loaded via index.html's own blocking
// <link>, not an import here, for the same reason (a module import would
// reopen the same deferred-timing gap for the stylesheet). AppData.tsx's
// effectiveTheme still owns the theme for everything after this first
// paint (explicit toggle, live system-preference changes).

// Legacy bare-path redirect (2026-09-30 Audit -> Analysis rename): the app
// is a hash router (lib/router.ts) — the server serves index.html for any
// unmatched path (main.py), so a plain, hash-less "/audit" link/bookmark
// from before the rename would otherwise just fall through to the live
// view like any other unrecognized path, same as any other bare path this
// SPA never routes on. Rewrite it to the equivalent "#/analysis" hash
// before the router's first read of location.hash, so that old link still
// lands on the right page. lib/router.ts's own parseRoute separately keeps
// accepting "#/audit" hashes forever, for bookmarks that already carry a
// hash.
(function redirectLegacyAuditPath() {
  const path = window.location.pathname;
  if (path === "/audit" || path.startsWith("/audit/")) {
    const rest = path.slice("/audit".length);
    const newHash = `#/analysis${rest}${window.location.search}`;
    window.history.replaceState(null, "", `/${newHash}`);
  }
})();

// PersistQueryClientProvider (lib/queryClient.ts) holds children's first
// render until the IndexedDB-persisted cache has been restored into
// `queryClient` — so by the time AppData's useQuery calls below ever run,
// last-known data (if any) is already in the cache and they resolve
// "success" on the very first render, no flash of empty/loading state. See
// lib/queryClient.ts's own header comment for the persistence contract
// (what's persisted, the buster, the 7-day maxAge).
ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{ persister, buster: BUILD_VERSION, maxAge: PERSIST_MAX_AGE_MS, dehydrateOptions: { shouldDehydrateQuery } }}
    >
      <App />
    </PersistQueryClientProvider>
  </React.StrictMode>,
);
